/**
 * JavaScript Bundle Optimizer
 * Conditionally loads heavy JS bundles based on page needs and user interaction
 * Reduces initial bundle size and improves page load performance
 */
(function() {
  'use strict';

  // Track what's been loaded
  var loadedBundles = new Set();
  var pageType = window.T4Srequest?.page_type || 'index';
  
  /**
   * Load a script bundle with optimization
   * @param {string} src - Script URL
   * @param {Object} options - Loading options
   */
  function loadScriptBundle(src, options) {
    options = options || {};
    
    // Guard: Prevent loading scripts with 'none' or invalid URLs
    if (!src || src === 'none' || src === 'undefined' || src.trim() === '' || src.indexOf('/none') > -1) {
      return Promise.resolve(); // Silently skip invalid scripts
    }
    
    if (loadedBundles.has(src)) {
      return Promise.resolve();
    }
    
    return new Promise(function(resolve, reject) {
      // Check if script already exists in DOM
      var existing = document.querySelector('script[src="' + src + '"]');
      if (existing) {
        loadedBundles.add(src);
        resolve();
        return;
      }

      var script = document.createElement('script');
      script.src = src;
      
      if (options.defer) {
        script.defer = true;
      }
      if (options.async) {
        script.async = true;
      }

      script.onload = function() {
        loadedBundles.add(src);
        resolve();
      };

      script.onerror = function() {
        reject(new Error('Failed to load script: ' + src));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Conditionally load scripts based on page type and features needed
   */
  function loadConditionalScripts() {
    var configs = window.T4Sconfigs;
    if (!configs) return;

    // Always load core scripts (already handled by theme)
    // Only load feature-specific scripts when needed
    
    // Product page specific scripts
    if (pageType === 'product') {
      if (configs.script5 && configs.script5 !== 'none') { // t4s_zoom.min.js
        loadScriptBundle(configs.script5, { defer: true }).catch(function() {});
      }
      if (configs.script12b && configs.script12b !== 'none') { // threesixty.min.js
        loadScriptBundle(configs.script12b, { defer: true }).catch(function() {});
      }
    }

    // Collection/search page scripts
    if (pageType === 'collection' || pageType === 'search') {
      if (configs.script7 && configs.script7 !== 'none') { // facets.min.js
        loadScriptBundle(configs.script7, { defer: true }).catch(function() {});
      }
      if (configs.script8 && configs.script8 !== 'none') { // nouislider.min.js
        loadScriptBundle(configs.script8, { defer: true }).catch(function() {});
      }
    }

    // Load instant page script only after initial load
    if (configs.script10 && configs.script10 !== 'none') { // t4s-instant-page.min.js
      if ('requestIdleCallback' in window) {
        requestIdleCallback(function() {
          loadScriptBundle(configs.script10, { defer: true }).catch(function() {});
        }, { timeout: 3000 });
      } else {
        setTimeout(function() {
          loadScriptBundle(configs.script10, { defer: true }).catch(function() {});
        }, 3000);
      }
    }
  }

  /**
   * Load non-critical scripts during idle time
   */
  function loadNonCriticalScripts() {
    var configs = window.T4Sconfigs;
    if (!configs) return;

    // Load these scripts only when browser is idle
    var nonCriticalScripts = [
      configs.script12, // reviewOther.js
      configs.script12a, // t4s-currencies.min.js
      configs.script11 // custom.js (if enabled)
    ].filter(function(src) {
      return src && src !== 'none';
    });

    if (nonCriticalScripts.length === 0) return;

    function loadNext() {
      if (nonCriticalScripts.length === 0) return;

      var src = nonCriticalScripts.shift();
      loadScriptBundle(src, { defer: true }).catch(function() {});

      if ('requestIdleCallback' in window) {
        requestIdleCallback(loadNext, { timeout: 2000 });
      } else {
        setTimeout(loadNext, 500);
      }
    }

    // Wait for user interaction or idle time
    var interacted = false;
    function onInteraction() {
      if (interacted) return;
      interacted = true;
      if ('requestIdleCallback' in window) {
        requestIdleCallback(loadNext, { timeout: 1000 });
      } else {
        setTimeout(loadNext, 1000);
      }
    }

    // Load on first user interaction
    ['click', 'scroll', 'keydown', 'touchstart', 'mousemove'].forEach(function(event) {
      document.addEventListener(event, onInteraction, { once: true, passive: true });
    });

    // Fallback: load after page is interactive
    if (document.readyState === 'complete') {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(loadNext, { timeout: 3000 });
      } else {
        setTimeout(loadNext, 3000);
      }
    } else {
      window.addEventListener('load', function() {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(loadNext, { timeout: 3000 });
        } else {
          setTimeout(loadNext, 3000);
        }
      }, { once: true });
    }
  }

  /**
   * Defer non-critical theme scripts based on page type and user interaction
   * PERFORMANCE: DISABLED - Removing scripts from DOM breaks functionality and causes delays
   * Scripts are already deferred via defer attribute, no need to remove and reload
   */
  function deferNonCriticalThemeScripts() {
    // DISABLED: Removing scripts from DOM and reloading causes:
    // 1. Broken functionality (scripts may have dependencies)
    // 2. Additional network requests
    // 3. Delayed execution
    // 4. Race conditions
    
    // Scripts are already properly deferred via defer attribute in theme.liquid
    // No additional optimization needed
  }

  // PERFORMANCE: Initialize after page is fully loaded to avoid blocking initial render
  // NOTE: deferNonCriticalThemeScripts() is intentionally disabled - scripts are already deferred via defer attribute
  // BUG FIX: Check readyState in else branch to handle race condition where script loads after load event
  function initOptimizer() {
    setTimeout(function() {
      loadConditionalScripts();
      loadNonCriticalScripts();
    }, 500);
  }
  
  if (document.readyState === 'complete') {
    // Page already loaded - run immediately
    initOptimizer();
  } else {
    // Wait for load event, but also check readyState in case event already fired
    if (document.readyState === 'complete') {
      initOptimizer();
    } else {
      window.addEventListener('load', initOptimizer, { once: true });
    }
  }

  // Expose utility globally
  window.KTJSBundleOptimizer = {
    loadScriptBundle: loadScriptBundle,
    loadConditionalScripts: loadConditionalScripts,
    loadNonCriticalScripts: loadNonCriticalScripts,
    deferNonCriticalThemeScripts: deferNonCriticalThemeScripts
  };
})();

